import React from "react";
import { useBluelagoon } from "@bluelagoon";
import { useTranslation } from "react-i18next";
import { Button } from "@shadcn-in-nx/ui/button";
import MainLayout from "../Layouts/main";
import {
    Table,
    TableBody,
    TableHead,
    TableHeader,
    TableRow,
} from "@shadcn-in-nx/ui/table";
import Icon from "../components/Icon";
import Pagination from "../components/Pagination";
import CreateDatabaseOrTableModal from "./CreateDatabaseOrTableModal";
import CloneDatabaseModal from "./CloneDatabaseModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import { useAuth } from "../context/AuthContext";
import PlanRestrictionAlert from "../Chat/ChatTwoColumnPage/PlanRestrictionAlert";
import DatabaseRow from "./components/DatabaseRow";
import DatabaseTableRow from "./components/DatabaseTableRow";
import DatabaseSearch from "./components/DatabaseSearch";
import useDatabaseActions from "./hooks/useDatabaseActions";

function Databases() {
    const { t } = useTranslation();
    const { hasActiveSubscription, isAdmin, isInTrial } = useAuth();
    const [searchQuery, setSearchQuery] = React.useState("");
    const [expandedDatabases, setExpandedDatabases] = React.useState(new Set());
    const { call, response, isLoading } = useBluelagoon({
        method: "getDatabases",
    });
    const actions = useDatabaseActions(call);

    React.useEffect(() => {
        call();
    }, [call]);

    const toggleExpanded = (id) => {
        setExpandedDatabases((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const filteredDatabases = React.useMemo(() => {
        const databases = response?.data?.databases || [];
        if (!searchQuery.trim()) return databases;
        const query = searchQuery.toLowerCase();
        return databases.filter(
            (db) =>
                db.name.toLowerCase().includes(query) ||
                db.tables?.some((t) => t.name.toLowerCase().includes(query))
        );
    }, [response?.data?.databases, searchQuery]);

    const { paginatedDatabases, totalPages, currentPage, setCurrentPage } =
        usePagination(filteredDatabases, 25);

    const isActive = hasActiveSubscription || isInTrial;

    return (
        <MainLayout>
            <div className="space-y-4">
                {!isActive && (
                    <PlanRestrictionAlert
                        variant="inline"
                        titleKey="databases.upgradeRequired"
                        descriptionKey="databases.freeVersionRestriction"
                    />
                )}
                <div
                    className={
                        !isActive
                            ? "pointer-events-none opacity-50 select-none"
                            : ""
                    }
                >
                    <DatabaseHeader
                        count={filteredDatabases.length}
                        isLoading={isLoading}
                        onCreateDatabase={actions.openDatabaseModal}
                    />
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-4 border-b">
                        <DatabaseSearch
                            value={searchQuery}
                            onChange={setSearchQuery}
                        />
                        <span className="text-sm text-muted-foreground">
                            {!isLoading &&
                                t("databases.databasesCount", {
                                    count: filteredDatabases.length,
                                })}
                        </span>
                    </div>

                    {!isLoading && filteredDatabases.length === 0 ? (
                        <EmptyState searchQuery={searchQuery} />
                    ) : (
                        <div className="space-y-4 mt-4">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>
                                            {t("databases.tableHeaders.name")}
                                        </TableHead>
                                        <TableHead className="hidden md:table-cell">
                                            {t(
                                                "databases.tableHeaders.aiDescription"
                                            )}
                                        </TableHead>
                                        <TableHead className="hidden lg:table-cell">
                                            {t(
                                                "databases.tableHeaders.connectedApps"
                                            )}
                                        </TableHead>
                                        <TableHead>
                                            {t(
                                                "databases.tableHeaders.lastUpdated"
                                            )}
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {isLoading ? (
                                        <LoadingSkeleton />
                                    ) : (
                                        paginatedDatabases.map((db) => (
                                            <React.Fragment key={db.id}>
                                                <DatabaseRow
                                                    database={db}
                                                    isExpanded={expandedDatabases.has(
                                                        db.id
                                                    )}
                                                    onToggle={() =>
                                                        toggleExpanded(db.id)
                                                    }
                                                    onAddTable={() =>
                                                        actions.openTableModal(
                                                            db
                                                        )
                                                    }
                                                    onClone={() =>
                                                        actions.openCloneModal(
                                                            db
                                                        )
                                                    }
                                                    onDelete={() =>
                                                        actions.openDeleteModal(
                                                            db,
                                                            "database"
                                                        )
                                                    }
                                                />
                                                {expandedDatabases.has(db.id) &&
                                                    db.tables?.map((table) => (
                                                        <DatabaseTableRow
                                                            key={table.id}
                                                            table={table}
                                                            databaseId={db.id}
                                                            onDelete={() =>
                                                                actions.openDeleteModal(
                                                                    table,
                                                                    "table"
                                                                )
                                                            }
                                                        />
                                                    ))}
                                            </React.Fragment>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                            {filteredDatabases.length > 0 && (
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={setCurrentPage}
                                    itemsPerPage={25}
                                    totalItems={filteredDatabases.length}
                                    className="pt-4"
                                />
                            )}
                        </div>
                    )}
                    <actions.Modals isAdmin={isAdmin} />
                </div>
            </div>
        </MainLayout>
    );
}

function DatabaseHeader({ onCreateDatabase }) {
    const { t } = useTranslation();
    return (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h1 className="page-title">{t("databases.title")}</h1>
            <Button
                onClick={onCreateDatabase}
                className="btn btn-primary w-full sm:w-auto"
            >
                <Icon name="Plus" className="w-4 h-4" />
                {t("databases.createDatabase")}
            </Button>
        </div>
    );
}

function EmptyState({ searchQuery }) {
    const { t } = useTranslation();
    return (
        <div className="text-center p-8 border border-gray-100 rounded-lg mt-4">
            <Icon
                className="w-12 h-12 mx-auto text-gray-400 mb-4"
                name="Database"
            />
            <h3 className="text-lg font-medium text-gray-900 mb-1">
                {searchQuery
                    ? t("databases.noResultsFound", "No databases found")
                    : t("databases.noDatabasesAvailable")}
            </h3>
            <p className="text-gray-500 mb-4">
                {searchQuery
                    ? t(
                          "databases.tryDifferentSearch",
                          "Try a different search term"
                      )
                    : t("databases.noDatabasesDescription")}
            </p>
        </div>
    );
}

function LoadingSkeleton() {
    return Array.from({ length: 3 }).map((_, i) => (
        <TableRow key={i}>
            {[32, 20, 40, 16].map((w, j) => (
                <td key={j} className="p-4">
                    <div
                        className={`animate-pulse h-4 w-${w} bg-gray-200 rounded`}
                    />
                </td>
            ))}
        </TableRow>
    ));
}

function usePagination(items, itemsPerPage) {
    const [currentPage, setCurrentPage] = React.useState(1);
    React.useEffect(() => {
        setCurrentPage(1);
    }, [items]);
    const totalPages = Math.ceil(items.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedDatabases = items.slice(
        startIndex,
        startIndex + itemsPerPage
    );
    return { paginatedDatabases, totalPages, currentPage, setCurrentPage };
}

export default Databases;
